import { Component, ElementRef, QueryList, ViewChildren, output, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FseService } from '../../fse/fse.service';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'ft-otp-verification',
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './otp-verification.html',
    styleUrl: './otp-verification.scss'
})
export class OtpVerificationComponent {
    verificationSuccess = output<void>();

    private readonly numInputs = 6;
    private fseService = inject(FseService);

    private destroyRef = inject(DestroyRef);
    #dialogRef = inject(MatDialogRef);

    @ViewChildren('otpInput') inputs!: QueryList<ElementRef>;

    otpForm = new FormGroup({
        otp: new FormArray(
            Array.from({ length: this.numInputs }, () =>
                new FormControl('', [Validators.required, Validators.pattern('[0-9]')])
            )
        )
    });

    verificationStatus: { type: 'success' | 'error', message: string } | null = null;

    get otpControls() {
        return this.otpForm.get('otp') as FormArray;
    }

    getControl(index: number) {
        return this.otpControls.at(index) as FormControl;
    }

    handleKeyDown(event: KeyboardEvent, index: number) {
        const inputElements = this.inputs.toArray();
        if (event.key === 'Backspace' && !this.getControl(index).value && index > 0) {
            inputElements[index - 1].nativeElement.focus();
        } else if (event.key === 'Delete' && !this.getControl(index).value && index < this.numInputs - 1) {
            inputElements[index + 1].nativeElement.focus();
        }
    }

    handleInput(event: any, index: number) {
        const value = event.target.value;
        if (/^[0-9]$/.test(value)) {
            this.verificationStatus = null;
            if (index < this.numInputs - 1) {
                this.inputs.toArray()[index + 1].nativeElement.focus();
            } else {
                this.onVerify();
            }
        } else if (value === '') {
            this.verificationStatus = null;
        } else {
            this.getControl(index).setValue('');
        }
    }

    onVerify() {
        const otp = this.otpControls.value.join('');
        if (otp.length !== this.numInputs) {
            this.verificationStatus = { type: 'error', message: 'Veuillez entrer les 6 chiffres.' };
            return;
        }

        const otpRequest = { otp, username: '' };

        this.fseService.verifyOTP(otpRequest).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response) => {
                if (response && response.verified) {

                    this.verificationStatus = { type: 'success', message: 'OTP valide. Redirection...' };
                    setTimeout(() => {
                        this.verificationSuccess.emit();
                        this.verificationStatus = null;
                        this.#dialogRef.close();
                    }, 2000);
                } else {
                    this.verificationStatus = { type: 'error', message: response?.message || 'OTP invalide. Veuillez réessayer.' };
                }
            },
            error: () => {
                this.verificationStatus = { type: 'error', message: 'OTP invalide. Veuillez réessayer.' };
            }
        });
    }

    onResend(event: Event) {
        event.preventDefault();
        this.fseService.resendOTP().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response) => {
                this.verificationStatus = (response && response.sent) ? { type: 'success', message: 'Un nouveau OTP a été envoyé.' } : { type: 'error', message: 'Échec de l\'envoi de l\'OTP.' };
            },
            error: () => this.verificationStatus = { type: 'error', message: 'Échec de l\'envoi de l\'OTP.' }
        });
    }
}
